<template>
  <div class="my-files-container">
    <!-- 圆形退出按钮 -->
    <div class="logout-button-container">
      <button class="logout-button" @click="handleLogout" title="退出登录">
        <i class="el-icon-switch-button"></i>
      </button>
    </div>
    
    <!-- 页面标题 -->
    <div class="page-title">
      <el-button 
          icon="el-icon-back" 
          type="text" 
          @click="goBack"
          class="back-btn"
        >
          返回上一页
      </el-button>
      <h2>我的文件管理</h2>
      <p>让每一份文件井然有序，随时找到需要的内容。</p>
    </div>
    <!-- 搜索和分类区域 -->
    <div class="search-and-category card">
      <el-row :gutter="20">
        <el-col :xs="24" :sm="24" :md="10" :lg="8" :xl="6">
          <el-input 
            v-model="searchText" 
            placeholder="搜索文件名" 
            prefix-icon="el-icon-search"
            clearable
            @clear="fetchFiles"
            @keyup.enter.native="fetchFiles"
            class="search-input"
          >
          </el-input>
        </el-col>
        <el-col :xs="24" :sm="24" :md="14" :lg="16" :xl="18">
          <el-tabs 
            v-model="currentCategory" 
            @tab-click="handleCategoryChange"
            type="card"
            class="category-tabs"
          >
            <el-tab-pane label="全部文件" name="0"></el-tab-pane>
            <el-tab-pane label="共享文件" name="shared"></el-tab-pane>
            <el-tab-pane 
              v-for="category in categories" 
              :key="category.id" 
              :label="category.categoryName" 
              :name="category.id.toString()"
            ></el-tab-pane>
          </el-tabs>
        </el-col>
      </el-row>
    </div>

    <!-- 文件列表区域 -->
    <div class="file-list-container card">
      <el-table 
        :data="fileList" 
        stripe 
        v-loading="loading"
        empty-text="暂无文件数据"
        :row-style="{ height: '50px' }"
        :header-cell-style="headerCellStyle"
        @sort-change="handleSortChange"
      >
        <el-table-column prop="fileName" label="文件名称" min-width="200" sortable="custom"
          :sort-orders="['ascending', 'descending', null]">
          <template slot-scope="scope"><file-identity :name="scope.row.fileName" /></template>
        </el-table-column>
        <el-table-column prop="fileSize" label="文件大小" width="105" sortable="custom"
          :sort-orders="['ascending', 'descending', null]">
          <template slot-scope="scope">
            {{ formatSize(scope.row.fileSize) }}
          </template>
        </el-table-column>
        <el-table-column prop="uploadTime" label="上传时间" width="160" sortable="custom"
          :sort-orders="['ascending', 'descending', null]"></el-table-column>
        <el-table-column label="共享状态" width="100">
          <template slot-scope="scope">
            <el-switch 
              v-model="scope.row.shared" 
              @change="handleShareChange(scope.row)"
              :active-value="true"
              :inactive-value="false"
              :disabled="scope.row.isDeleting"
            ></el-switch>
          </template>
        </el-table-column>
        <el-table-column prop="sharedEndDate" label="共享结束时间" width="145"></el-table-column>
        <el-table-column label="删除" width="76">
          <template slot-scope="scope">
            <el-button 
              type="text" 
              icon="el-icon-delete" 
              @click="handleDelete(scope.row)"
              :loading="scope.row.isDeleting"
              class="delete-btn"
              aria-label="将文件移入回收站"
              title="移入回收站"
            ></el-button>
          </template>
        </el-table-column>
        <el-table-column label="下载" width="100">
          <template slot-scope="scope">
            <!-- 使用带进度条的下载组件 -->
            <file-download-with-progress
              :file-id="scope.row.fileId"
              :file-name="scope.row.fileName"
              :file-size="scope.row.fileSize"
            ></file-download-with-progress>
          </template>
        </el-table-column>
        <div slot="empty" class="workspace-empty"><span class="empty-icon"><i class="el-icon-folder-opened"></i></span><h3>这里还没有文件</h3><p>试试其他分类，或上传一份新文件。</p><router-link to="/file/upload" class="section-link">上传文件 <i class="el-icon-right"></i></router-link></div>
      </el-table>

      <!-- 分页控件 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.pageNum"
          :page-sizes="[5, 10, 20, 50]"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
        ></el-pagination>
      </div>
    </div>

    <!-- 共享持续时间对话框 -->
    <el-dialog 
      title="设置共享持续时间" 
      v-model="shareDialogVisible"
      :close-on-click-modal="false"
      :visible.sync="shareDialogVisible"
      @close="handleDialogClose" 
      width="400px"
      >
      <el-form :model="shareForm" label-width="120px">
        <el-form-item label="持续时间">
          <el-select v-model="shareForm.duration" placeholder="请选择共享持续时间">
            <el-option 
              v-for="option in shareForm.durationOptions" 
              :key="option.value" 
              :label="option.label" 
              :value="option.value"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="handleCancelShare">取消</el-button>
        <el-button type="primary" @click="confirmShare">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCategories, getFileList, deleteFile, updateShare } from '@/api/file'
import { SHARE_DURATION_OPTIONS, DEFAULT_SHARE_DURATION } from '@/constants/shareOptions'
import { logout as logoutApi } from '@/api/auth';
import { removeToken } from '@/utils/auth';
import FileDownloadWithProgress from '@/components/FileDownloadWithProgress'
import FileIdentity from '@/components/FileIdentity'

export default {
  name: 'MyFiles',
  components: {
    FileIdentity,
    FileDownloadWithProgress // 注册下载进度条组件
  },
  data() {
    return {
      categories: [],
      fileList: [],
      currentCategory: '0',
      searchText: '',
      loading: false,
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      shareDialogVisible: false,
      currentFile: null,
      shareForm: {
        duration: DEFAULT_SHARE_DURATION,
        durationOptions: SHARE_DURATION_OPTIONS
      },
      sharedFiles: 3,
       // 新增排序参数
      sortParams: {
        field: null,  // 排序字段：name/size/uploadTime
        order: null   // 排序方向：asc/desc
      }
    };
  },
  created() {
    this.fetchCategories();
    this.fetchFiles();
  },
  methods: {
    // 退出登录
    handleLogout() {
      this.$confirm('确定要退出登录吗?', '确认退出', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 调用后端退出登录API
        logoutApi().then(() => {
          // 清除本地token
          removeToken();
          // 跳转到登录页
          this.$router.push('/login');
          this.$message.success('退出登录成功');
        }).catch(() => {
          // 即使API调用失败，也清除token并跳转
          removeToken();
          this.$router.push('/login');
          this.$message.success('退出登录成功');
        });
      }).catch(() => {
        // 用户取消退出
      });
    },
    
    goBack() {
      window.history.back();
    },
    headerCellStyle() {
      return {
        'background-color': '#f5f7fa',
        'font-weight': 'bold',
        'color': '#303133'
      };
    },
    
    formatSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    fetchCategories() {
      getCategories().then(res => {
        this.categories = res.data;
      }).catch(err => {
        this.$message.error('获取分类失败: ' + (err.message || err));
      });
    },

    fetchFiles() {
      this.loading = true;
      if(this.currentCategory === "shared") {
        this.sharedFiles = 1;
        this.currentCategory = "0";
      } else {
        this.sharedFiles = 3;
      }
      const params = {
        categoryId: this.currentCategory,
        pageNum: this.pagination.pageNum,
        pageSize: this.pagination.pageSize,
        searchText: this.searchText,
        isShared: this.sharedFiles,
        // 添加排序参数
        sortField: this.sortParams.field,
        sortOrder: this.sortParams.order
      };

      getFileList(params).then(res => {
        this.fileList = res.data.records || [];
        this.pagination.total = res.data.total || 0;
      }).catch(err => {
        this.$message.error('获取文件列表失败: ' + (err.message || err));
      }).finally(() => {
        this.loading = false;
      });
    },

    handleCategoryChange() {
      this.pagination.pageNum = 1;
      this.fetchFiles();
    },

    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.pageNum = 1;
      this.fetchFiles();
    },

    handleCurrentChange(pageNum) {
      this.pagination.pageNum = pageNum;
      this.fetchFiles();
    },

    handleShareChange(file) {
      // 深拷贝文件对象
      const currentFile = { ...file };
      if (currentFile.shared) {
        // 开启共享 - 显示弹窗
        this.currentFile = currentFile;
        this.shareDialogVisible = true;
      } else {
        // 关闭共享 - 直接调用API
        this.updateShareStatus(currentFile.fileId, false);
      }
    },

    confirmShare() {
      if (!this.currentFile) {
        console.error('[confirmShare] currentFile为空');
        this.shareDialogVisible = false;
        return;
      }
      
      this.isShareConfirmed = true;  // 标记为已确认
      console.log('[confirmShare] 确认共享设置:', this.shareForm.duration);
      
      this.updateShareStatus(
        this.currentFile.fileId, 
        true, 
        this.shareForm.duration
      ).then(() => {
        this.shareDialogVisible = false;
        this.$message.success('共享设置成功');
      }).catch(err => {
        console.error('[confirmShare] 共享设置失败:', err);
        this.$message.error('共享设置失败: ' + (err.message || err));
        this.resetShareStatus();  // 失败时恢复状态
      });
    },

    updateShareStatus(fileId, isShared, duration = 0) {
      return updateShare(fileId, {
        shared: isShared,
        shareDuration: duration
      }).then(() => {
        this.fetchFiles();
      }).catch(err => {
        this.$message.error('共享设置失败: ' + (err.message || err));
        this.resetShareStatus();
        const index = this.fileList.findIndex(f => f.fileId === fileId);
        if (index !== -1) {
          this.fileList[index].shared = !isShared;
        }
      });
    },

    handleDelete(file) {
      this.$confirm(`确定要删除文件"${file.fileName}"吗？`, '删除确认', {
        type: 'warning',
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      }).then(() => {
        file.isDeleting = true;
        deleteFile(file.fileId).then(() => {
          this.$message.success('删除成功');
          this.fetchFiles();
        }).catch(err => {
          this.$message.error('删除失败: ' + (err.message || err));
        }).finally(() => {
          file.isDeleting = false;
        });
      });
    },

    // 新增排序处理方法
    handleSortChange({ column, order }) {
      // 重置排序参数
      this.sortParams = { field: null, order: null };
      
      if (order) { // 如果有排序方向
        // 根据列设置排序参数
        if (column.label === '文件名称') {
          this.sortParams = { field: 'fileName', order: order === 'ascending' ? 'asc' : 'desc' };
        } else if (column.label === '文件大小') {
          this.sortParams = { field: 'fileSize', order: order === 'ascending' ? 'asc' : 'desc' };
        } else if (column.label === '上传时间') {
          this.sortParams = { field: 'uploadTime', order: order === 'ascending' ? 'asc' : 'desc' };
        }
        
        // 重新加载数据
        this.fetchFiles();
      }
    },

    handleCancelShare() {
      // 用户点击取消按钮
      this.shareDialogVisible = false;
      // 不需要额外调用resetShareStatus，@close事件会处理
    },

    handleDialogClose() {
      // 所有关闭场景都会触发此方法
      console.log('[handleDialogClose] 弹窗关闭，确认状态:', this.isShareConfirmed);
      
      if (!this.isShareConfirmed) {
        // 用户未确认，恢复共享状态
        this.resetShareStatus();
      }
      
      // always reset the flag
      this.isShareConfirmed = false;
    },

    resetShareStatus() {
      if (!this.currentFile || !this.currentFile.fileId) return;
      const fileId = this.currentFile.fileId;
      // 使用id精确定位文件
      const index = this.fileList.findIndex(function (item) {
        return item.fileId === fileId;
        });
      if (index !== -1) {
        // 创建新对象并替换，触发响应式更新
        const updatedFile = { ...this.fileList[index] };
        updatedFile.shared = false;
        this.fileList.splice(index, 1, updatedFile);
      }
    },

  }

};
</script>

<style scoped>
/* 容器样式 */
.my-files-container {
  max-width: 1200px; /* 限制最大宽度 */
  margin: 0 auto; /* 水平居中 */
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
  position: relative;
}

/* 圆形退出按钮容器 */
.logout-button-container {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 100;
}

/* 圆形退出按钮样式 */
.logout-button {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background-color: #f56c6c;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(245, 108, 108, 0.3);
  transition: all 0.3s ease;
}

.logout-button:hover {
  background-color: #f78989;
  box-shadow: 0 6px 16px rgba(245, 108, 108, 0.4);
}

.logout-button:active {
  transform: scale(0.95);
}

/* 页面标题 */
.page-title {
  margin-bottom: 20px;
  color: #303133;
}

/* 卡片样式 */
.card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 20px;
  margin-bottom: 20px;
}

/* 搜索和分类区域 */
.search-and-category {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* 搜索框样式 */
.search-input {
  width: 100%;
}

/* 分类标签样式 */
.category-tabs {
  width: 100%;
}

/* 文件列表区域 */
.file-list-container {
  overflow: hidden;
}

/* 表格样式 */
::v-deep .el-table {
  border-radius: 4px;
  overflow: hidden;
}

::v-deep .el-table__header th {
  background-color: #f5f7fa;
  font-weight: 500;
}

::v-deep .el-table tr:hover > td {
  background-color: #f9fafc !important;
}

/* 分页样式 */
.pagination-container {
  margin-top: 20px;
  text-align: right;
}

/* 删除按钮样式 */
.delete-btn {
  color: #ff4d4f;
}

.delete-btn:hover {
  color: #ff7875;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .my-files-container {
    padding: 10px;
  }
  
  .card {
    padding: 15px;
  }
  
  .page-title h2 {
    font-size: 18px;
  }
  
  ::v-deep .el-table {
    font-size: 12px;
  }
  
  .pagination-container {
    text-align: center;
  }
}
</style>
